import { A } from '@adornis/base/env-info.js';
import { alertDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { openAustrittDialog } from '../../../austritt/client/AustrittDialog.js';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { useMartinusABAC } from '../../../permissions/client/useMartinusAbac.js';
import { UserListAPI } from '../../../users/list/api/UserListAPI.js';
import { UserListActions } from '../../../users/list/UserListPolicy.js';
import { UserListSelectionInterface } from '../../../users/list/entities/UserListSelectionInterface.js';
import { BookingAPI } from '../../api/BookingAPI.js';
import type { BookingHashIngnoreKey } from '../../api/bookingHash.js';
import { Booking } from '../../entities/Booking.js';
import { SGMassBookingCard } from './sg-mass-booking-card.js';
import type { GroupedBooking, GroupedBookingBooking } from './useGroupedBookings.js';
import { useGroupedBookings } from './useGroupedBookings.js';
import { PageHeader } from '@martinus/styles/client/PageHeader.js';

const loadingSkeleton = html`
  <div class="flex flex-col gap-4">
    ${repeat(
      Array.from({ length: 6 }),
      () => html`
        <div class="card p-4 flex flex-row flex-card items-center gap-4">
          <div class="skeleton h-6 flex grow"></div>
        </div>
      `,
    )}
  </div>
`;

const allIgnoreKeys: BookingHashIngnoreKey[] = ['userID', 'grade', 'classIdentifier', 'billingPosition', '_id'];

const keyTranslation = {
  userID: 'Person',
  grade: 'Jahrgangsstufe',
  classIdentifier: 'Klassenbezeichnung',
  billingPosition: 'Abrechnung',
  _id: 'Keine Gruppierung',
};

export const SGMassBookingEditor = stateful(() => {
  const [listSelection, setListSelection] = useState<UserListSelectionInterface | null>(null);
  const [newBookings, setNewBookings] = useState<GroupedBooking[]>([]);
  const [ignoreGroupingKeys, setIgnoreGroupingKeys] = useState<BookingHashIngnoreKey[]>(['userID', '_id']);

  const [openSpecificBookingID, setOpenSpecificBookingID] = useState<string | null>(null);

  const [collapsedActiveBookings, setCollapsedActiveBookings] = useState(false);
  const [collapsedInactiveBookings, setCollapsedInactiveBookings] = useState(true);

  let createdNew = false;

  const { can: canExit } = useMartinusABAC({ action: UserListActions.USER_LIST_BOOKINGS_EDIT });

  useEffect(() => {
    try {
      // set user selection
      const filterDoc = JSON.parse(routingStateSignal.value.searchParams.get('selection') || '{}');
      if (!filterDoc || (!filterDoc.userIDs?.length && !filterDoc.filter)) return;
      const listSelection = new UserListSelectionInterface({ ...filterDoc });
      setListSelection(listSelection);
    } catch (error) {
      if (error instanceof Error)
        alertDialog(
          `Es ist ein Fehler bei der Personenauswahl aufgetreten. Ihre URL scheint nicht richtig zu sein. Fehler: ${error.message}`,
        );
    }
  }, [routingStateSignal.value]);

  const { value: persons, loading: personsLoading } = useQuery(
    () =>
      UserListAPI.getBySelection(listSelection)({
        _id: 1,
        debitorennummer: 1,
        profilDaten: { stammdaten: { vorname: 1, nachname: 1 } },
      }),
    [listSelection],
  );

  useEffect(() => {
    // consume possible create booking param
    const createBooking = routingStateSignal.value.searchParams.get('createBooking');
    if (createBooking && !createdNew && persons?.length) {
      setNewBookings([
        {
          users: persons,

          // for every person create a booking
          bookings: persons.map(p => new Booking({ userID: p._id, groupIDs: [createBooking] })),

          // as new bookings are not saved and do not have any id, we use a globally unique id to just have something
          hash: A.getGloballyUniqueID(),
          active: false,
          label: 'Neue Buchung',
        } satisfies GroupedBooking,
      ]);
      setCollapsedActiveBookings(true);
      setCollapsedInactiveBookings(true);
      createdNew = true;
    }

    // consume possible open booking param
    const openBooking = routingStateSignal.value.searchParams.get('openBooking');
    if (openBooking && !openSpecificBookingID) {
      setOpenSpecificBookingID(openBooking);
      setCollapsedActiveBookings(true);
      setCollapsedInactiveBookings(true);
    }
  }, [routingStateSignal.value, persons]);

  const { value: bookings, loading: bookingsLoading } = useQuery(
    () =>
      BookingAPI.subscribeForSelection(listSelection)({
        _id: 1,
        userID: 1,
        createdAt: 1,
        groupIDs: 1,
        groups: {
          name: 1,
          parentIDs: 1,
          parents: { parentIDs: 1, parents: { parentIDs: 1 } },
          billingPosition: {
            name: 1,
            _id: 1,
            priceInCents: 1,
            rhythm: 1,
            kuerzel: 1,
            kostenstelleID: 1,
            buchungskonto: 1,
            templateID: 1,
          },
        },
        startDate: 1,
        endDate: 1,
        classIdentifier: 1,
        grade: 1,
        bookingReference: 1,
        billingPosition: { priceInCents: 1 },
      }),
    [listSelection],
  );

  const { groupedBookings, loading: groupingLoading } = useGroupedBookings(
    bookings as unknown as GroupedBookingBooking[] | null,
    persons,
    ignoreGroupingKeys,
    listSelection,
  );

  const activeBookings = groupedBookings.filter(
    booking => booking.active && booking.bookings[0]?._id !== openSpecificBookingID,
  );
  const inactiveBookings = groupedBookings.filter(
    booking => !booking.active && booking.bookings[0]?._id !== openSpecificBookingID,
  );

  const specificBooking = groupedBookings.find(booking => booking.bookings[0]?._id === openSpecificBookingID);

  const handleCreateBooking = () => {
    setCollapsedActiveBookings(true);
    setCollapsedInactiveBookings(true);
    const newBooking: GroupedBooking = {
      users: persons ?? [],

      // for every person create a booking
      bookings: persons?.map(p => new Booking({ userID: p._id })) ?? [],

      // as new bookings are not saved and do not have any id, we use a globally unique id to just have something
      hash: A.getGloballyUniqueID(),
      active: false,
      label: 'Neue Buchung',
    };
    setNewBookings([...newBookings, newBooking]);
  };

  return html`
    ${PageHeader({
      title: 'Buchungen editieren',
      // spans, not a div: the subtitle renders inside a <p>
      subtitle: html`<span class="inline-flex flex-wrap items-center gap-2">
        ${
          (persons?.length ?? 0) <= 7
            ? repeat(
                (persons ?? []).slice(0, 3),
                p => p._id,
                p => html`
                  <span
                    class="cursor-pointer underline hover:text-primary"
                    @click=${() => {
                      goTo(`/personen/${p._id}`);
                    }}
                  >
                    ${p.profilDaten?.stammdaten?.vorname} ${p.profilDaten?.stammdaten?.nachname}
                  </span>
                `,
              )
            : nothing
        }
        <span class="font-semibold">
          ${persons?.length ?? '---'} ${(persons?.length ?? 0) === 1 ? 'Person' : 'Personen'} ausgewählt
        </span>
      </span>`,
    })}

    <div class="flex flex-col gap-4 m-4">
      <div class="card p-4 flex flex-col flex-card gap-4">
        <div class="flex flex-col gap-2">
          <p>
            Buchungen werden nach verschiedenen Attributen gruppiert um sie gesammelt zu bearbeiten. Hier können die
            Attribute eingestellt werden, eine andere Gruppierung zu erzeugen.
          </p>
          <p>
            Sollte ein Attribut <b>nicht</b> in die Gruppierung mit einfließen, kann es <b>nicht</b> mehr bearbeitet
            werden!
          </p>
        </div>

        <div class="flex flex-row justify-between gap-8">
          <div class="flex flex-row gap-2 items-center">
            <span class="text-sm font-semibold text-base-content/70">Gruppierung:</span>
            ${repeat(
              // we never want to turn of the userID filter, as this is the base case
              allIgnoreKeys.filter(key => key !== 'userID'),
              key => key,
              key => {
                const isActive = !ignoreGroupingKeys.includes(key);
                const groupingDeactivated = !ignoreGroupingKeys.includes('_id') && key !== '_id';
                return html`<div
                  class="badge transition-all hover:scale-105 hover:brightness-110 cursor-pointer ${
                    groupingDeactivated ? 'badge-warning' : isActive ? 'badge-success' : 'badge-error'
                  }"
                  @click=${() => {
                    if (groupingDeactivated) return;
                    if (isActive) {
                      setIgnoreGroupingKeys([...ignoreGroupingKeys, key]);
                    } else {
                      setIgnoreGroupingKeys(ignoreGroupingKeys.filter(k => k !== key));
                    }
                  }}
                >
                  ${keyTranslation[key]}
                </div>`;
              },
            )}
          </div>

          <div class="flex flex-row gap-2">
            ${
              canExit
                ? html`<button
                    class="btn btn-error btn-outline"
                    ?disabled=${persons?.length !== 1}
                    title="Beendet die Mitgliedschaften und SEPA-Mandate eines Schülers"
                    @click=${async () => {
                      const personID = persons?.length === 1 ? persons[0]?._id : null;
                      if (!personID) return;
                      const ended = await openAustrittDialog(personID);
                      if (ended !== null)
                        void SgSnackbar.show(`Austritt durchgeführt: ${ended} Mitgliedschaften beendet oder gelöscht`);
                    }}
                  >
                    <span class="icon-[material-symbols--person-remove-rounded]"></span>
                    Austritt
                  </button>`
                : nothing
            }
            <button class="btn btn-success" @click=${handleCreateBooking}>
              <span class="icon-[material-symbols--add]"></span>
              Neue Buchung
            </button>
          </div>
        </div>
      </div>
      ${groupingLoading || personsLoading || bookingsLoading ? loadingSkeleton : nothing}
      ${repeat(
        newBookings,
        booking => booking.hash,
        booking =>
          SGMassBookingCard({
            booking,
            ignoreGroupingKeys,
            groupSize: newBookings.length,
            forceOpen:
              newBookings.find(b => b.hash === booking.hash) !== undefined ||
              (!!openSpecificBookingID && booking.bookings[0]?._id === openSpecificBookingID),
            forceClose: !!openSpecificBookingID && booking.bookings[0]?._id !== openSpecificBookingID,
            savedCallback: () => {
              setNewBookings(newBookings.filter(b => b.hash !== booking.hash));
            },
          }),
      )}
      ${
        specificBooking
          ? SGMassBookingCard({
              booking: specificBooking,
              ignoreGroupingKeys,
              groupSize: activeBookings.length,
              forceOpen: true,
              savedCallback: () => {
                setNewBookings(newBookings.filter(b => b.hash !== specificBooking.hash));
              },
            })
          : nothing
      }

      <button
        type="button"
        class="flex w-full cursor-pointer items-center justify-between gap-3 rounded-box px-1 py-2 text-left hover:bg-base-200/40"
        @click=${() => setCollapsedActiveBookings(!collapsedActiveBookings)}
      >
        <div class="flex items-center gap-2">
          <span
            class="icon-[material-symbols--expand-more-rounded] inline-block h-4 w-4 shrink-0 text-base-content/70 transition-transform duration-200 ${
              collapsedActiveBookings ? '' : 'rotate-180'
            }"
          ></span>
          <span class="text-sm font-semibold uppercase tracking-wide text-base-content/70">Aktive Buchungen</span>
        </div>
        <span class="badge badge-ghost badge-sm"> ${activeBookings.length} Buchungen </span>
      </button>

      <div
        class="grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
          collapsedActiveBookings ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
        }"
      >
        <div class="min-h-0 overflow-hidden">
          <div class="flex flex-col gap-4 px-0.5 pt-1">
            ${repeat(
              activeBookings,
              booking => booking.hash,
              booking =>
                SGMassBookingCard({
                  booking,
                  ignoreGroupingKeys,
                  groupSize: activeBookings.length,
                  savedCallback: () => {
                    setNewBookings(newBookings.filter(b => b.hash !== booking.hash));
                  },
                }),
            )}
          </div>
        </div>
      </div>

      <button
        type="button"
        class="flex w-full cursor-pointer items-center justify-between gap-3 rounded-box px-1 py-2 text-left hover:bg-base-200/40"
        @click=${() => setCollapsedInactiveBookings(!collapsedInactiveBookings)}
      >
        <div class="flex items-center gap-2">
          <span
            class="icon-[material-symbols--expand-more-rounded] inline-block h-4 w-4 shrink-0 text-base-content/70 transition-transform duration-200 ${
              collapsedInactiveBookings ? '' : 'rotate-180'
            }"
          ></span>
          <span class="text-sm font-semibold uppercase tracking-wide text-base-content/70">Inaktive Buchungen</span>
        </div>
        <span class="badge badge-ghost badge-sm"> ${inactiveBookings.length} Buchungen </span>
      </button>

      <div
        class="grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
          collapsedInactiveBookings ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
        }"
      >
        <div class="min-h-0 overflow-hidden">
          <div class="flex flex-col gap-4 px-0.5 pt-1">
            ${repeat(
              inactiveBookings,
              booking => booking.hash,
              booking =>
                SGMassBookingCard({
                  booking,
                  ignoreGroupingKeys,
                  groupSize: inactiveBookings.length,
                  savedCallback: () => {
                    setNewBookings(newBookings.filter(b => b.hash !== booking.hash));
                  },
                }),
            )}
          </div>
        </div>
      </div>
    </div>
  `;
});
